/**
 * The version of the NATIVE shell the user is actually running.
 *
 * Deliberately distinct from `APP_VERSION` (src/version.ts), which is the JS
 * bundle's version, inlined by Vite from package.json. A Capgo OTA replaces the
 * JS bundle WITHOUT replacing the native binary, so `APP_VERSION` can report a
 * version the device is not really running. Anything that makes a decision
 * about native capability — the update gate, and the adoption tracking that
 * tells us when it is safe to raise the gate's floor — must use this instead.
 *
 * Uses @capacitor/app's getInfo(), which is a cheap local read of Info.plist /
 * build.gradle. It does NOT hit the store, so it is safe on the login hot path.
 * (The gate separately uses getAppUpdateInfo(), which DOES hit the store,
 * because it also needs to know whether an update is actually available.)
 */

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import { logWarn } from './log';

export interface NativeVersionInfo {
  /** 'ios' | 'android' | 'web' */
  platform: string;
  /** Native version name, or null on web / when unreadable. */
  nativeVersion: string | null;
  /** Native build number, or null on web / when unreadable. */
  nativeBuild: string | null;
}

export async function getNativeVersionInfo(): Promise<NativeVersionInfo> {
  const platform = Capacitor.getPlatform();
  if (!Capacitor.isNativePlatform()) {
    return { platform, nativeVersion: null, nativeBuild: null };
  }
  try {
    const info = await App.getInfo();
    return { platform, nativeVersion: info.version, nativeBuild: info.build };
  } catch (error) {
    logWarn('[nativeVersion] App.getInfo() failed', error);
    return { platform, nativeVersion: null, nativeBuild: null };
  }
}
